/**
 * `POST /invitations/accept` (a bearer token in the body) and
 * `POST /invitations/{id}/accept` (no body -- the caller's own verified
 * address is the selector) -- the two tenant-scoped writes with no tenant in
 * their URL, because the caller is not a member yet and nothing server-side
 * can gate either on a membership. One hook for both: they return the same
 * union and the caller tells them apart by which variable it passes.
 *
 * It reads `status` and `tenant_id` from the 200 body; a refusal is data, not
 * a thrown error. The response has not committed to returning a tenant name,
 * and building the confirmation around one would be a contract nobody agreed
 * to. The tenant's name arrives with the refreshed `/control/me`, which this
 * flow invalidates anyway: the caller went from no membership to one in this
 * single request, and the shell renders its navigation and campaign switcher
 * from the refreshed listing at `/`.
 *
 * `already_accepted` also invalidates `/control/me` rather than navigating:
 * that status only reaches this hook through the by-id path, on a row the
 * caller already holds a membership for (a double click, a second tab), and
 * the membership is real -- the shell's own data should reflect it without
 * forcing the caller off the list they were reading.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import type { ApiFailureError } from '../../../lib/http/errors';
import { HOME_PATH } from '../../routes/paths';
import { useAuthState } from '../../session/authState';
import { useApiClient } from '../../session/client';
import { writeLastUsed } from '../../session/lastUsed';
import { ME_QUERY_KEY } from '../../session/session';
import { acceptanceSchema, type AcceptResponse } from './schemas';

export const ACCEPT_PATH = '/invitations/accept';

export type AcceptInvitationInput = { token: string } | { invitationId: string };

export function useAcceptInvitation(): UseMutationResult<
  AcceptResponse,
  ApiFailureError,
  AcceptInvitationInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const uid = useAuthState().user?.uid ?? null;

  return useMutation<AcceptResponse, ApiFailureError, AcceptInvitationInput>({
    // React Query retains `variables` as `mutation.state.variables` on the
    // app-wide client after a mutation settles, which outlives the screen that
    // owns the token — and on the success path that screen has already
    // unmounted. `token.ts` promises the string it returns is the only copy;
    // this is what keeps that true.
    gcTime: 0,
    mutationFn: (input) =>
      'token' in input
        ? client.postParsed(ACCEPT_PATH, { token: input.token }, acceptanceSchema)
        : client.postParsed(
            `/invitations/${input.invitationId}/accept`,
            undefined,
            acceptanceSchema,
          ),
    onSuccess: async (data) => {
      if (data.status === 'accepted') {
        // Recorded before `/` resolves the landing, so a campaign-scoped
        // invitation lands on its campaign rather than on an organisation home the
        // invitee may not read (platform design §17.8).
        if (uid !== null) writeLastUsed(uid, { tenantId: data.tenant_id, campaignId: null });
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        await navigate({ to: HOME_PATH });
      } else if (data.status === 'already_accepted') {
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      }
    },
  });
}
